/**
 * Centralized Logger using loglevel
 *
 * This replaces logger.debug statements with a configurable logger that can be:
 * - Completely disabled in production
 * - Set to different log levels (trace, debug, info, warn, error)
 * - Easily controlled via environment variables
 * - Send errors to external monitoring services in production
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/utils/logger';
 *
 * // Instead of: logger.debug('User clicked button', data);
 * logger.info('User clicked button', data);
 *
 * // Instead of: console.error('API failed', error);
 * logger.error('API failed', error);
 * ```
 *
 * Production Strategy:
 * - debug/info/log: Completely silent (no console output)
 * - warn: Visible in console + sent to Sentry via custom override (for monitoring widget warnings only)
 * - error: Visible in console + sent to Sentry via custom override (for tracking/alerting with full context)
 *
 * Local/Test Strategy:
 * - debug/info/warn/error: All visible in console for debugging
 */

import log from 'loglevel';
import { getDeploymentTarget } from '../../config/deployment';

// Define valid log levels for type safety
type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent';

// Valid log level values for runtime validation
const VALID_LOG_LEVELS: readonly LogLevel[] = [
    'trace',
    'debug',
    'info',
    'warn',
    'error',
    'silent',
] as const;

/**
 * Validates if a string is a valid log level
 */
function isValidLogLevel(level: string): level is LogLevel {
    return VALID_LOG_LEVELS.includes(level as LogLevel);
}

type ErrorContext = Record<string, unknown>;

type RoseStorageLogLevelStore = {
    getNamedLogLevel(name: string): string | null;
    getLogLevel(): string | null;
    setNamedLogLevel(name: string, level: LogLevel): void;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

// Lazy getter for Rose storage to avoid circular dependency
let roseStorageInstance: RoseStorageLogLevelStore | null = null;
let roseStorageLoading: Promise<RoseStorageLogLevelStore | null> | null = null;

async function getRoseStorageAsync() {
    if (roseStorageInstance) {
        return roseStorageInstance;
    }

    if (roseStorageLoading) {
        return roseStorageLoading;
    }

    roseStorageLoading = (async () => {
        try {
            // Dynamic import to avoid circular dependency (rose-storage imports logger)
            // Note: Vite warns this won't code-split since rose-storage is statically imported elsewhere,
            // but the dynamic import here is intentional to break the circular dependency at initialization time
            const module = await import('../../services/rose-storage');
            roseStorageInstance = module.roseStorage;
            return roseStorageInstance;
        } catch (error) {
            // If import fails, just use loglevel's default behavior
            console.warn('Failed to load Rose storage for logger persistence:', error);
            return null;
        }
    })();

    return roseStorageLoading;
}

// Configure log level based on environment
// Default to production (silent) if NODE_ENV is not set
// Use import.meta.env for Vite apps, fallback to process.env for Node.js/webpack
const getNodeEnv = (): string => {
    // Vite uses import.meta.env
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) {
        return import.meta.env.MODE;
    }
    // Node.js / webpack uses process.env
    if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
        return process.env.NODE_ENV;
    }
    // Default to production for safety
    return 'production';
};

const nodeEnv = getNodeEnv();
const isDevelopment = nodeEnv === 'development';
const isTest = nodeEnv === 'test';
const isProduction = !isDevelopment && !isTest; // Everything else is production

// Check if running in playground (window.location.hostname includes 'localhost' or '127.0.0.1')
// Note: During WXT build, window exists but window.location may be undefined
const isPreprod =
    typeof window !== 'undefined' &&
    typeof window.location !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.port === '5173'); // Vite default port

// Determine default log level - warn in production (show warnings/errors), debug in development or preprod
let defaultLevel: LogLevel = 'warn';
if (isTest) {
    defaultLevel = 'silent';
} else if (isDevelopment || isPreprod) {
    defaultLevel = 'debug';
}
// Production defaults to 'warn' (shows warn and error logs only)

/**
 * Clean up loglevel's default localStorage keys (loglevel creates these automatically)
 */
const cleanupLoglevelKeys = () => {
    try {
        // Remove loglevel's auto-created keys
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith('loglevel')) {
                keysToRemove.push(key);
            }
        }
        for (const key of keysToRemove) {
            localStorage.removeItem(key);
        }
    } catch (_error) {
        // Silently fail - not critical
    }
};

/**
 * Execute a function while preventing loglevel from writing to localStorage
 */
const withoutLoglevelPersistence = <T>(fn: () => T): T => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return fn();
    }

    // In test environments (like jsdom), localStorage.setItem may not be reassignable
    // Check if we can override it before attempting
    const originalSetItem = localStorage.setItem;
    let canOverride = true;

    try {
        // Temporarily override setItem to block loglevel keys
        localStorage.setItem = (key: string, value: string): void => {
            if (key.startsWith('loglevel')) {
                // Silently ignore loglevel's persistence attempts
                return;
            }
            // Allow all other keys
            originalSetItem.call(localStorage, key, value);
        };
    } catch {
        // localStorage.setItem cannot be overridden (e.g., in jsdom test environment)
        canOverride = false;
    }

    try {
        return fn();
    } finally {
        // Restore original setItem if we were able to override it
        if (canOverride) {
            try {
                localStorage.setItem = originalSetItem;
            } catch {
                // Silently ignore if restoration fails
            }
        }
    }
};

// Try to load log level from Rose storage first, then fall back to environment/defaults
let initialLevel: LogLevel = defaultLevel;

// Only load from storage in development - in production always use default
if (!isProduction) {
    // Start loading Rose storage asynchronously (don't wait for it)
    getRoseStorageAsync().then((storage) => {
        if (storage) {
            try {
                // Try to get the named logger level first (Rose), then default level
                const storedNamedLevel = storage.getNamedLogLevel('Rose');
                const storedDefaultLevel = storage.getLogLevel();

                const storedLevel = storedNamedLevel || storedDefaultLevel;
                if (storedLevel && isValidLogLevel(storedLevel)) {
                    // Update log level if storage has a different value from initial
                    if (storedLevel !== initialLevel) {
                        withoutLoglevelPersistence(() => {
                            log.setLevel(storedLevel);
                            logger.setLevel(storedLevel);
                        });
                        cleanupLoglevelKeys();
                    }
                }
            } catch (_error) {
                // Silently fail
            }
        }
    });
}

// Allow runtime override via environment variable (highest priority)
const getLogLevelOverride = (): string | undefined => {
    // Vite uses import.meta.env with VITE_ prefix
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LOG_LEVEL) {
        return import.meta.env.VITE_LOG_LEVEL;
    }
    // Node.js / webpack uses process.env
    if (typeof process !== 'undefined' && process.env?.LOG_LEVEL) {
        return process.env.LOG_LEVEL;
    }
    return undefined;
};
const logLevelOverride = getLogLevelOverride();
if (logLevelOverride && logLevelOverride.trim() !== '') {
    if (isValidLogLevel(logLevelOverride)) {
        initialLevel = logLevelOverride;
    } else {
        console.warn(
            'Invalid LOG_LEVEL:',
            logLevelOverride,
            '. Valid levels are:',
            VALID_LOG_LEVELS.join(', ')
        );
    }
}

// Set the initial log level WITHOUT loglevel persistence
withoutLoglevelPersistence(() => {
    log.setLevel(initialLevel);
});

// Create named logger with prefix for better debugging
const logger = log.getLogger('Rose');

// Sync logger level with Rose storage WITHOUT loglevel persistence
withoutLoglevelPersistence(() => {
    logger.setLevel(initialLevel);
});

// Clean up any existing loglevel keys
cleanupLoglevelKeys();

// Enhanced error reporting for production
interface ErrorReporter {
    reportError(error: Error | string, context?: ErrorContext): void;
}

class ProductionErrorReporter implements ErrorReporter {
    reportError(error: Error | string, context?: ErrorContext): void {
        // Send to Sentry for error tracking (best tool for the job)
        // Note: rw_domain is already set globally via setSentryAnalyticsContext()
        // Sentry automatically captures: URL, user agent, timestamp, browser info, stack trace

        // Import Sentry functions directly from the sentry service module
        // This avoids circular dependencies by using dynamic import
        import('../../services/sentry')
            .then(({ captureException, captureMessage }) => {
                if (error instanceof Error) {
                    captureException(error, { extra: context });
                } else {
                    captureMessage(error, { level: 'error', extra: context });
                }
            })
            .catch((err) => {
                // Fallback: log to console if Sentry import fails
                console.error('Failed to import Sentry:', err);
                console.error('Original error:', error, context);
            });
    }
}

// Enable error reporting in all environments (development, staging, production)
// This helps catch errors early and provides better debugging context
const errorReporter = new ProductionErrorReporter();

// Customize the logger output format to always show Rose branding
// NOTE: Must come BEFORE logger.error override, as setLevel() regenerates methods
if (typeof window !== 'undefined') {
    // Use a function to avoid circular dependency issues
    const setupCustomFormat = () => {
        const originalFactory = logger.methodFactory;
        logger.methodFactory = (methodName, logLevel, loggerName) => {
            const rawMethod = originalFactory(methodName, logLevel, loggerName);
            return (...args) => {
                const timestamp = new Date().toISOString().slice(11, 23);

                // Get caller location by analyzing the stack trace (only in development for performance)
                let callerLocation = '';
                if (isDevelopment) {
                    try {
                        const stack = new Error().stack;
                        if (stack) {
                            const stackLines = stack.split('\n');

                            for (const line of stackLines) {
                                if (
                                    line.includes('at ') &&
                                    !line.includes('logger.ts') &&
                                    !line.includes('loglevel') &&
                                    !line.includes('methodFactory') &&
                                    !line.includes('rawMethod')
                                ) {
                                    const match = line.match(/at\s+.*\s+\((.+):(\d+):(\d+)\)/);
                                    if (match) {
                                        const [, filePath, lineNum] = match;
                                        const cleanFilePath = filePath.split('?')[0];
                                        const fileName =
                                            cleanFilePath.split('/').pop() || cleanFilePath;
                                        callerLocation = `${fileName}:${lineNum}`;
                                        break;
                                    }
                                }
                            }
                        }
                    } catch (_error) {
                        // Silently fail if stack trace analysis fails
                    }
                }

                // Get emoji based on deployment target
                // 🌹 for widget/preprod/demo, 🔌 for chrome-extension
                const deploymentTarget = getDeploymentTarget();
                const emoji = deploymentTarget === 'chrome-extension' ? '🔌' : '🌹';

                // Format based on environment
                if (isDevelopment) {
                    // Development: Show timestamp, skip logger name if it's just "Rose"
                    const loggerNamePart =
                        String(loggerName) === 'Rose' ? '' : ` [${String(loggerName)}]`;
                    rawMethod(
                        `${emoji} [${timestamp}] [${String(methodName).toUpperCase()}]${loggerNamePart}${callerLocation ? ` ${callerLocation}` : ''}`,
                        ...args
                    );
                } else {
                    // Production: Just emoji and log level
                    rawMethod(`${emoji} [${String(methodName).toUpperCase()}]`, ...args);
                }
            };
        };
        logger.setLevel(logger.getLevel()); // Apply the custom format
    };

    // Call the setup function
    setupCustomFormat();
}

// Override warn method to include external reporting in all environments
// IMPORTANT: Must come AFTER setupCustomFormat() because setLevel() regenerates all methods
const originalWarn = logger.warn.bind(logger);
logger.warn = (message: unknown, ...args: unknown[]) => {
    // Always log to console (visible in all environments)
    originalWarn(message, ...args);

    // Also report to Sentry in all environments
    if (errorReporter) {
        try {
            // Import Sentry captureMessage function
            import('../../services/sentry')
                .then(({ captureMessage }) => {
                    const warningMessage = typeof message === 'string' ? message : String(message);
                    const context = args.length > 0 ? { additionalData: args } : undefined;
                    captureMessage(warningMessage, { level: 'warning', extra: context });
                })
                .catch((err) => {
                    // Fallback: silently fail if Sentry import fails
                    console.error('Failed to import Sentry for warning:', err);
                });
        } catch (_reportError) {
            // Prevent error reporting failures from disrupting logging flow
            // Silently fail to avoid double warnings
        }
    }
};

// Override error method to include external reporting in all environments
// IMPORTANT: Must come AFTER setupCustomFormat() because setLevel() regenerates all methods
const originalError = logger.error.bind(logger);
logger.error = (message: unknown, ...args: unknown[]) => {
    // Always log to console (visible in all environments)
    originalError(message, ...args);

    // Also report to external services (Sentry) in all environments
    if (errorReporter) {
        try {
            let error: Error;
            let context: ErrorContext | undefined;

            // Handle different argument patterns
            if (message instanceof Error) {
                // Pattern 1: logger.error(errorObject, contextObject)
                error = message;
                context = args.length > 0 && isRecord(args[0]) ? args[0] : undefined;
            } else if (args.length > 0 && args[0] instanceof Error) {
                // Pattern 2: logger.error('message', errorObject, ...otherContext)
                // This is the cleanest and most common pattern
                error = args[0];
                context = {
                    errorMessage: message,
                    ...(args.length > 1 && isRecord(args[1]) ? args[1] : {}),
                };
            } else if (args.length > 0 && isRecord(args[0]) && args[0].error instanceof Error) {
                // Pattern 3: logger.error('message', { error: errorObject, ...context })
                error = args[0].error;
                context = { ...args[0], errorMessage: message };
                if (context) {
                    delete context.error; // Remove error from context to avoid duplication
                }
            } else {
                // Pattern 4: logger.error('message', ...otherArgs) - create new Error
                const errorMessage = typeof message === 'string' ? message : String(message);
                error = new Error(errorMessage);
                context = args.length > 0 ? { additionalData: args } : undefined;
            }

            errorReporter.reportError(error, context);
        } catch (_reportError) {
            // Prevent error reporting failures from disrupting logging flow
            // Silently fail to avoid double errors
        }
    }
};

export { logger };

// Export convenience methods for common use cases
export const logInfo = logger.info.bind(logger);
export const logError = logger.error.bind(logger);
export const logWarn = logger.warn.bind(logger);
export const logDebug = logger.debug.bind(logger);

// Export error reporter for direct use when needed
// Note: logger.error already handles external reporting to Sentry,
// so we just delegate to it to avoid double-reporting
export const reportError = (error: Error | string, context?: ErrorContext) => {
    logger.error(error, context);
};

/**
 * Set log level for the Rose logger and persist to Rose storage
 */
export const setLogLevel = async (level: LogLevel): Promise<boolean> => {
    try {
        // Validate log level
        if (!isValidLogLevel(level)) {
            console.error(
                'Invalid log level:',
                level,
                '. Valid levels are:',
                VALID_LOG_LEVELS.join(', ')
            );
            return false;
        }

        // Set in loglevel library WITHOUT allowing it to persist to localStorage
        withoutLoglevelPersistence(() => {
            log.setLevel(level);
            logger.setLevel(level);
        });

        // Clean up any existing loglevel keys
        cleanupLoglevelKeys();

        // Persist to Rose storage instead
        try {
            const storage = await getRoseStorageAsync();
            if (storage) {
                storage.setNamedLogLevel('Rose', level);
            }
        } catch (storageError) {
            // Log warning but don't fail - loglevel will still work
            console.warn('Failed to persist log level to Rose storage:', storageError);
        }

        logger.info(`Log level set to: ${level}`);
        return true;
    } catch (error) {
        console.error('Failed to set log level:', error);
        return false;
    }
};

// Export function to enable debug mode dynamically (uses setLogLevel)
export const enableDebugMode = () => {
    return setLogLevel('debug');
};

// Export function to check current log level
export const getLogLevel = () => {
    try {
        return log.getLevel();
    } catch (error) {
        console.warn('Could not get log level:', error);
        return null;
    }
};
